import {
    useQuery,
    UseQueryResult,
    useMutation,
    UseMutationResult,
    useQueryClient,
    QueryClient,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import {
    userFiltersSchema,
    saveUserFiltersResponseSchema,
    type UserFilters,
    type SaveUserFiltersResponse,
} from './locationTaskUserFilter.types';

// ---- Query Key Factory ----

export const locationTaskUserFilterKeys = {
    all: ['locationTaskUserFilters'] as const,
    list: () => [...locationTaskUserFilterKeys.all, 'list'] as const,
};

export const invalidateUserFilters = (qc: QueryClient) =>
    qc.invalidateQueries({ queryKey: locationTaskUserFilterKeys.all });

// ---- Queries ----

const listUserFilters = async (): Promise<UserFilters> => {
    const response = await createPacerApi().get(`${API_ROOT}location-task-user-filter/`).json();
    return userFiltersSchema.parse(response);
};

export const useListUserFilters = (): UseQueryResult<UserFilters> =>
    useQuery<UserFilters>({
        queryKey: locationTaskUserFilterKeys.list(),
        queryFn: listUserFilters,
    });

// ---- Mutations ----

const saveUserFilters = async (request: UserFilters): Promise<SaveUserFiltersResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task-user-filter/`, { json: request })
        .json();
    return saveUserFiltersResponseSchema.parse(response);
};

export const useSaveUserFilters = (
    callback?: (result: SaveUserFiltersResponse) => void
): UseMutationResult<SaveUserFiltersResponse, Error, UserFilters> => {
    const queryClient = useQueryClient();
    return useMutation<SaveUserFiltersResponse, Error, UserFilters>({
        mutationFn: saveUserFilters,
        onSuccess: (result) => {
            callback?.(result);
            invalidateUserFilters(queryClient);
        },
    });
};
